import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClipboardCheck, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from '../utils/toast';
import EvaluationFormModal from '@/components/EvaluationFormModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ManagerEvaluationDashboard({ isEmbedded = false }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      if (parsedUser.role !== 'Manager' && parsedUser.role !== 'Reporting Manager') {
        toast.error('Access denied. This page is for Managers only.');
        navigate('/');
        return;
      }
      
      fetchEvaluations();
    } else {
      navigate('/signin');
    }
  }, [navigate]);

  const fetchEvaluations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/evaluations/manager/my-evaluations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvaluations(response.data.evaluations || []);
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      toast.error('Failed to load evaluations');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { 
        label: 'Pending', 
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: Clock
      },
      in_progress: { 
        label: 'In Progress', 
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: Clock
      },
      completed: { 
        label: 'Completed', 
        className: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.className} border inline-flex items-center gap-1 whitespace-nowrap`}>
        <Icon className="w-3 h-3 shrink-0" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  const handleStartEvaluation = (evaluation) => {
    // Open modal instead of navigating to a new page
    setSelectedEvaluation(evaluation);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEvaluation(null);
  };

  const handleEvaluationSuccess = () => {
    // Refresh evaluations after successful submission
    fetchEvaluations();
  };

  if (loading) {
    const loadingContent = (
      <div className="p-6">
        <div className="text-center">Loading evaluations...</div>
      </div>
    );
    return isEmbedded ? loadingContent : <AdminLayout>{loadingContent}</AdminLayout>;
  }

  const dashboardContent = (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto space-y-4 sm:space-y-6 min-w-0 overflow-x-hidden">
        {/* Header */}
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 break-words">My Evaluation Forms</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 break-words">
            Complete assigned evaluations for your team members
          </p>
        </div>

        {/* Evaluations List */}
        {evaluations.length === 0 ? (
          <Card className="min-w-0">
            <CardContent className="p-6 sm:p-8 md:p-12 text-center min-w-0">
              <ClipboardCheck className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 break-words">
                No Evaluations Assigned
              </h3>
              <p className="text-sm sm:text-base text-gray-500 break-words">
                You currently have no evaluation forms to complete.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {evaluations.map((evaluation) => (
              <Card key={evaluation.instance_id} className="hover:shadow-lg transition-shadow min-w-0">
                <CardContent className="p-4 sm:p-5 md:p-6 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col min-[380px]:flex-row min-[380px]:items-start gap-2 min-[380px]:gap-3 mb-2 min-w-0">
                        <ClipboardCheck className="w-5 h-5 sm:w-6 sm:h-6 text-[#0056B3] shrink-0 mt-0.5" />
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words min-w-0 flex-1">
                          {evaluation.template_name || 'Staff and Department Evaluation'}
                        </h3>
                        <div className="shrink-0">{getStatusBadge(evaluation.status)}</div>
                      </div>
                      
                      <div className="grid grid-cols-1 min-[390px]:grid-cols-2 gap-3 sm:gap-4 mt-4 text-sm min-w-0">
                        <div className="min-w-0">
                          <span className="text-gray-500">Institution:</span>
                          <p className="font-medium text-gray-900 break-words">{evaluation.hospital_name}</p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-gray-500">Triggered By:</span>
                          <p className="font-medium text-gray-900 break-words">{evaluation.triggered_by_name}</p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-gray-500">Assigned On:</span>
                          <p className="font-medium text-gray-900 break-words">{formatDate(evaluation.created_at)}</p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-gray-500">Evaluation ID:</span>
                          <p className="font-mono text-xs text-gray-600 break-all">
                            #{evaluation.instance_id.slice(0, 8)}
                          </p>
                        </div>
                      </div>

                      {evaluation.status === 'completed' && (
                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2 min-w-0">
                          <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                          <span className="text-sm text-green-800 break-words">
                            Completed on {formatDate(evaluation.completed_at || evaluation.updated_at)}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="sm:ml-4 w-full sm:w-auto shrink-0">
                      {evaluation.status === 'pending' || evaluation.status === 'in_progress' ? (
                        <Button
                          onClick={() => handleStartEvaluation(evaluation)}
                          className="w-full sm:w-auto bg-[#0056B3] hover:bg-[#0056B3]/90 min-h-10"
                        >
                          {evaluation.status === 'in_progress' ? 'Continue' : 'Start Evaluation'}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleStartEvaluation(evaluation)}
                          variant="outline"
                          className="w-full sm:w-auto min-h-10"
                        >
                          View Details
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Card */}
        {evaluations.length > 0 && (
          <Card className="bg-blue-50 border-blue-200 min-w-0">
            <CardContent className="p-4 min-w-0">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-start gap-2 break-words">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                About Evaluations
              </h3>
              <ul className="text-sm text-blue-800 space-y-1 break-words">
                <li>• Click "Start Evaluation" to complete the feedback form</li>
                <li>• You will rate your team members on Compliance, Skill Gap, and Patient Care</li>
                <li>• You will also answer department-level questions</li>
                <li>• Once submitted, the status will change to "Completed"</li>
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    );
  
  return (
    <>
      {isEmbedded ? dashboardContent : <AdminLayout>{dashboardContent}</AdminLayout>}
      
      {/* Evaluation Form Modal */}
      <EvaluationFormModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        evaluation={selectedEvaluation}
        onSuccess={handleEvaluationSuccess}
      />
    </>
  );
}
